import React, { FC } from "react";

import {
  EditFilled,
  PartitionOutlined,
  UnorderedListOutlined,
  BookOutlined,
  ShareAltOutlined,
} from "@ant-design/icons";
import { Button, Popover, Tooltip, Typography } from "antd";
import clsx from "clsx";

import { AccruedFee, formatPrice, formatPricing, RiskLevel, GpmThreshold } from "../../data-types";
import { HTMLLink } from "../active-matters";
import { Colors } from "../colors";

import { ProgressBar } from "../progress-bar";
import { GrossProfitMargin } from "./gross-profit-margin";

import styles from "./accrued-fee-list.module.css";
import { GpmSlider } from "../gpm-slider";
import { useCurrentUserData } from "~/hooks";
import { CurrentUserFeeEarnerDataQuery } from "~/gql/graphql";

const {
  ["col-1"]: columnOneClassName,
  ["edit-matter-link"]: editMatterLink,
  ["title-data"]: titleData,
  ["col-2"]: columnTwoClassName,
  ["list-item-container"]: listItemContainerClassName,
  ["accrued-time"]: accruedTime,
  ["over-budget"]: overBudgetClassName,
  ["accrued-chargeable-time"]: accruedChargeableTime,
  ["pricing"]: pricingClassName,
  ["gpm-slider"]: gpmSliderClassName,
  ["button-row"]: buttonRowClassName,
  ["title-row"]: titleRowClassName,
  ["matter-title-container"]: matterTitleContainerClassName,
  ["matter-title"]: matterTitleClassName,
} = styles as any;

const COLOR_BY_ALERT_LEVEL: Record<RiskLevel, string> = {
  [RiskLevel.None]: Colors.Success,
  [RiskLevel.LowRisk]: Colors.Warning,
  [RiskLevel.MediumRisk]: Colors.Warning,
  [RiskLevel.HighRisk]: Colors.Warning,
  [RiskLevel.Issue]: Colors.Error,
};

export type LiveMattersProps = {
  loading: boolean;
  item: AccruedFee;
  Link: HTMLLink;
  currencySymbol: string;
  onShowTimeLogs: (matterId: number) => void;
  onShowReport: (matterId: number) => void;
  showGpm: boolean;
  showGpmSlider: boolean;
  filterBy: "user" | "organisationalUnit";
};

export const LiveMatters: FC<LiveMattersProps> = ({
  item,
  Link,
  currencySymbol,
  onShowTimeLogs,
  onShowReport,
  showGpm,
  showGpmSlider,
  filterBy,
}) => {
  const isParent = (item.subMatters?.length ?? 0) > 0;
  const { currentUser } = useCurrentUserData({ cache: true });

  const { gpmDangerThreshold, gpmWarningThreshold } = item;

  const gpmThresholds = { gpmDangerThreshold, gpmWarningThreshold, source: "matter" };

  return (
    <div className={listItemContainerClassName}>
      <div className={columnOneClassName}>
        <div className={titleRowClassName}>
          <div className={titleData}>
            <Typography.Title level={5} style={{ marginBottom: 0 }}>
              {item.clientName}
            </Typography.Title>
            <div className={matterTitleContainerClassName}>
              <Typography.Text className={matterTitleClassName} ellipsis={true}>
                {item.matterTitle}
                {isParent && (
                  <Tooltip title={`This is a multi discipline proposal`} placement="bottomRight">
                    <PartitionOutlined style={{ marginLeft: 5 }} />
                  </Tooltip>
                )}
              </Typography.Text>
              {isSharedBy(item, currentUser) && (
                <Tooltip
                  title={getSharedByText(item.collaborators, currentUser)}
                  placement="bottomRight"
                >
                  <ShareAltOutlined style={{ marginLeft: 5, marginRight: 5 }} />
                </Tooltip>
              )}
              <Link
                to={isParent ? `/parent/${item.matterId}` : `/matter-editor/${item.matterId}`}
                className={editMatterLink}
              >
                <EditFilled style={{ fontSize: 17 }} />
              </Link>
            </div>
          </div>
          <div className={clsx(accruedTime, item.actualFeePercentage > 100 && overBudgetClassName)}>
            <span className={accruedChargeableTime}>Accrued Chargeable Time:</span>
            <div>
              {formatPrice(item.actualFee, currencySymbol)}
              {item.actualFeePercentage > 100
                ? " | +" + formatPrice(item.actualFeeVariance, currencySymbol)
                : undefined}
            </div>
          </div>
        </div>
        <Popover
          title="Accrued Time"
          content={
            <div>
              <div>Planned: {formatPrice(item.plannedFee, currencySymbol)}</div>
              <div>Actual: {formatPrice(item.actualFee, currencySymbol)}</div>
            </div>
          }
        >
          <div>
            <ProgressBar
              percent={item.actualFeePercentage}
              strokeColor={COLOR_BY_ALERT_LEVEL[item.actualFeeRiskLevel]}
            />
          </div>
        </Popover>
        <div className={pricingClassName}>
          {filterBy === "organisationalUnit" && item?.leadFeeEarner?.name && (
            <span>Lead: {item.leadFeeEarner.name} 👤</span>
          )}
          <div>
            <i>{formatPricing(item)}</i> {formatPrice(item.plannedFee, currencySymbol)}
          </div>
        </div>
      </div>
      <div className={columnTwoClassName}>
        {showGpm && <GrossProfitMargin gpm={item.actualGpm} gpmThreshold={gpmThresholds} />}
        {!(showGpm && item.actualGpm == null) &&
          showGpmSlider &&
          gpmThresholds.gpmDangerThreshold &&
          gpmThresholds.gpmWarningThreshold && (
            <div className={gpmSliderClassName}>
              <GpmSlider
                showGpm={showGpm}
                gpm={item.actualGpm}
                gpmThreshold={gpmThresholds as GpmThreshold}
                timeEntryCount={item.accruedTimes.length}
                isFeeEarnerLevelGpm={false}
                totalWidth={150}
              />
            </div>
          )}

        <div className={buttonRowClassName}>
          <Button
            icon={<UnorderedListOutlined />}
            type="primary"
            ghost
            onClick={() => onShowTimeLogs(item.matterId)}
            style={{ marginRight: 8 }}
          >
            Details
          </Button>
          <Button
            icon={<BookOutlined />}
            type="primary"
            onClick={() => onShowReport(item.matterId)}
          >
            Report
          </Button>
        </div>
      </div>
    </div>
  );
};

function isSharedBy(
  item: AccruedFee,
  currentUser: CurrentUserFeeEarnerDataQuery["currentUserFeeEarnerData"]
) {
  return (
    ((item.collaborators?.length ?? 0) > 0 &&
      item.collaborators?.some((x) => x.userId === currentUser?.userId)) ??
    false
  );
}

function getSharedByText(
  item: AccruedFee["collaborators"],
  currentUser: CurrentUserFeeEarnerDataQuery["currentUserFeeEarnerData"]
) {
  return `Shared by ${
    item?.find((x) => x.userId === currentUser?.userId)?.createdByUser?.displayName
  }`;
}
